
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ClientLicenseInfo {
  status: string;
}

interface Client {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  description?: string;
  status: 'active' | 'inactive';
  parent_client_id?: string | null;
  created_at: string;
  updated_at: string;
  client_licenses?: ClientLicenseInfo[];
  has_active_license?: boolean;
}

interface ClientFormData {
  name: string;
  email: string;
  phone: string;
  address: string;
  description?: string;
}

// Type for data coming from Supabase
interface ClientFromDB {
  id: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  description?: string;
  parent_client_id?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

// Helper function to convert DB data to Client type
const convertToClient = (dbClient: ClientFromDB): Client => ({
  ...dbClient,
  status: (dbClient.status === 'active' || dbClient.status === 'inactive') 
    ? dbClient.status as 'active' | 'inactive' 
    : 'active'
});

export const useClients = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: clients = [],
    isLoading: loading,
    error
  } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*, client_licenses(status)')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching clients:', error);
        throw error;
      }

      const convertedClients = (data as any[]).map(item => {
        const client = convertToClient(item as ClientFromDB);
        const rawLicenses = item.client_licenses;
        const licenses = Array.isArray(rawLicenses) 
          ? rawLicenses 
          : rawLicenses 
            ? [rawLicenses] 
            : [];
        client.has_active_license = licenses.some((l: any) => l.status === 'active');
        return client;
      });
      return convertedClients;
    },
  });

  const addClient = useMutation({
    mutationFn: async (clientData: ClientFormData) => {
      const { data, error } = await supabase
        .from('clients')
        .insert([clientData])
        .select()
        .single();

      if (error) {
        console.error('Error adding client:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({
        title: "Cliente cadastrado",
        description: `Cliente ${variables.name} foi cadastrado com sucesso.`,
      });
    },
    onError: (error) => {
      console.error('Error adding client:', error);
      toast({
        title: "Erro ao cadastrar cliente",
        description: "Não foi possível cadastrar o cliente.",
        variant: "destructive",
      });
    },
  });

  const updateClient = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<ClientFormData> }) => {
      const { data: updatedData, error } = await supabase
        .from('clients')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating client:', error);
        throw error;
      }

      return updatedData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({
        title: "Cliente atualizado",
        description: "Cliente foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error updating client:', error);
      toast({
        title: "Erro ao atualizar cliente",
        description: "Não foi possível atualizar o cliente.",
        variant: "destructive",
      });
    },
  });

  const deleteClient = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting client:', error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast({
        title: "Cliente excluído",
        description: "Cliente foi excluído com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error deleting client:', error);
      toast({
        title: "Erro ao excluir cliente",
        description: "Não foi possível excluir o cliente.",
        variant: "destructive",
      });
    },
  });

  return {
    clients,
    loading,
    error,
    addClient,
    updateClient,
    deleteClient,
  };
};
